import { Suspense, useEffect, useRef, useState } from "react";
import type { StoreApi } from "zustand";
import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
  type AnyRoute,
  type RouterHistory,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  ContentRowsSkeleton,
  createAppQueryClient,
  createPermissionEvaluator,
  createServiceContainer,
  ErrorBoundary,
  PageHeaderSkeleton,
  resolveAuthGuard,
  resolveGuestGuard,
  resolveLandingRoute,
  resolveFeatureNavigation,
  useAuthStore,
  useTenant,
  UnauthorizedScreen,
  type AppFeatureDefinition,
  type AuthGuardResult,
  type AuthController,
  type AuthRepository,
  type AuthState,
  type AuthUser,
  type FeatureErrorFallbackProps,
  type FeatureRegistry,
  type MutableServiceContainer,
  type RemoteFeatureFailure,
  type RemoteFeatureLoader,
  type ServiceContainer,
} from "@nasam/core";
import { Button } from "@nasam/ui";

import {
  authController as defaultAuthController,
  authHttpClient,
  authRepository as defaultAuthRepository,
  setForbiddenHandler,
} from "./auth";
import {
  ActivateAccountScreen,
  ForgotPasswordScreen,
  LinkStatusScreen,
  LoginScreen,
  OtpChallengeScreen,
  ResetOtpChallengeScreen,
  ResetPasswordScreen,
  type LoginScreenProps,
} from "./auth/screens";
import { featureRegistry as defaultModuleRegistry } from "./registry";
import { loadRuntimeFeatures } from "./runtimeFeatures";
import { AppShell } from "./shell";

export {
  ActivateAccountScreen,
  ForgotPasswordScreen,
  LinkStatusScreen,
  LoginScreen,
  OtpChallengeScreen,
  ResetOtpChallengeScreen,
  ResetPasswordScreen,
  type LoginScreenProps,
};

function throwRedirect(target: Parameters<typeof redirect>[0]): never {
  // TanStack Router redirects by throwing a specialized Response instance.
  // eslint-disable-next-line @typescript-eslint/only-throw-error
  throw redirect(target);
}

/**
 * Only same-site absolute paths survive the loading gate, so a crafted
 * `?redirect=` cannot bounce a visitor off to another origin.
 */
function safeInternalPath(value: unknown): string | null {
  if (typeof value !== "string") return null;
  if (!value.startsWith("/") || value.startsWith("//")) return null;
  return value;
}

/**
 * Any route reached before the silent refresh settles has to park at
 * /auth/loading, which would otherwise forget where the visitor was going.
 * That lost a guest's password-reset or activation link (they arrive by email
 * and carry their token in the URL), and dropped an authenticated visitor's
 * bookmark or shared link onto the landing module instead of the page asked
 * for. The requested path rides along so the gate can resume it.
 */
function gateBeforeLoad(
  guard: AuthGuardResult,
  location: { readonly pathname: string; readonly searchStr?: string },
): void {
  if (guard.allowed) return;

  // Built from the parts rather than location.href, which carries the origin
  // and would fail the same-site check below.
  const href = `${location.pathname}${location.searchStr ?? ""}`;

  if (guard.redirectTo === "/auth/loading") {
    // The route tree is built dynamically, so redirect() cannot infer this
    // route's search shape; validateSearch on /auth/loading is the check.
    throwRedirect({
      to: guard.redirectTo,
      search: { redirect: href } as never,
    });
  }
  throwRedirect({ to: guard.redirectTo });
}

export interface AuthLoadingScreenProps {
  readonly authStore?: StoreApi<AuthState>;
  /** Where to resume once the session settles, when the visitor asked for a
   *  specific screen (a reset link, or a bookmarked module page). */
  readonly pendingPath?: string | undefined;
}

export function AuthLoadingScreen({
  authStore = useAuthStore,
  pendingPath,
}: AuthLoadingScreenProps) {
  const { t } = useTranslation();
  const { branding } = useTenant();
  const navigate = useNavigate();
  const requested = safeInternalPath(pendingPath);
  const [initialized, setInitialized] = useState(
    () => authStore.getState().initialized,
  );
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const s = authStore.getState();
    return s.accessToken !== null && s.user !== null;
  });
  const [timedOut, setTimedOut] = useState(false);
  // Leaving this screen changes the pending path back to its default, which
  // would otherwise re-fire the effect and override the navigation just made.
  const hasLeft = useRef(false);

  useEffect(() => {
    return authStore.subscribe((state) => {
      setInitialized(state.initialized);
      setIsAuthenticated(state.accessToken !== null && state.user !== null);
    });
  }, [authStore]);

  useEffect(() => {
    if (initialized) {
      if (hasLeft.current) return;
      hasLeft.current = true;
      // The destination's own guard still runs, so resuming a path the
      // visitor may no longer be entitled to simply bounces them onward.
      void navigate({
        to: requested ?? (isAuthenticated ? "/" : "/auth/login"),
      });
      return;
    }

    const timer = setTimeout(() => {
      setTimedOut(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, [initialized, isAuthenticated, requested, navigate]);

  const handleProceedToLogin = () => {
    authStore.getState().setInitialized(true);
    void navigate({ to: "/auth/login" });
  };

  if (timedOut) {
    return (
      <div
        role="alert"
        className="animate-rise flex min-h-screen flex-col items-center justify-center p-6 text-center"
      >
        <div className="max-w-md space-y-4">
          <h1 className="text-heading-h3-bold text-text">
            {t("common:somethingWentWrong")}
          </h1>
          <Button variant="primary" onClick={handleProceedToLogin}>
            {t("common:confirm")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="animate-rise flex min-h-screen flex-col items-center justify-center p-6 text-center"
    >
      <div className="flex flex-col items-center space-y-4">
        <img
          src={branding?.logoUrl ?? "/brand/logo-mark.png"}
          alt=""
          className="size-12 animate-spin object-contain"
        />
        <p className="text-body-m-r text-text-placeholder">
          {t("common:loading")}
        </p>
      </div>
    </div>
  );
}

export function NoAccessScreen({
  authController = defaultAuthController,
}: {
  readonly authController?: AuthController;
}) {
  const { t } = useTranslation();

  const handleLogout = () => {
    void authController.logout("manual");
  };

  return (
    <div
      role="alert"
      className="animate-rise flex min-h-screen flex-col items-center justify-center p-6 text-center"
    >
      <div className="max-w-md space-y-4">
        <h1 className="text-heading-h3-bold text-text">
          {t("common:noModulesFound")}
        </h1>
        <p className="text-body-m-r text-text-placeholder">
          {t("common:somethingWentWrong")}
        </p>
        <Button variant="secondary" onClick={handleLogout}>
          {t("common:confirm")}
        </Button>
      </div>
    </div>
  );
}

export function UnauthorizedRouteScreen() {
  const navigate = useNavigate();

  return (
    <UnauthorizedScreen
      onGoHome={() => {
        void navigate({ to: "/" });
      }}
    />
  );
}

export function NotFoundScreen() {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className="animate-rise flex min-h-[18.75rem] flex-col items-center justify-center p-6 text-center"
    >
      <p className="text-body-l-b text-text">404</p>

      <p className="text-body-m-r text-text-placeholder">
        {t("common:somethingWentWrong")}
      </p>
    </div>
  );
}

// Generic by necessity: this is the Suspense fallback for every lazily-loaded
// feature screen, so it can't know the real layout coming in. A route that
// wants a closer match should pass its own pendingComponent instead.
function DefaultModuleFallback() {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-live="polite"
      className="animate-rise flex min-h-[12.5rem] w-full flex-col gap-6 p-6"
    >
      <span className="sr-only">{t("common:loading")}</span>
      <div aria-hidden="true" className="flex flex-col gap-6">
        <PageHeaderSkeleton actions={["w-24"]} />
        <ContentRowsSkeleton rows={5} />
      </div>
    </div>
  );
}

function DefaultModuleErrorFallback({
  error,
  reset,
}: FeatureErrorFallbackProps) {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className="animate-rise flex min-h-[12.5rem] flex-col items-center justify-center p-6 text-center"
    >
      <div className="max-w-md space-y-3">
        <p className="text-body-m-b text-fail-text">{error.message}</p>
        <Button variant="secondary" onClick={reset}>
          {t("common:confirm")}
        </Button>
      </div>
    </div>
  );
}

function createModuleRoutes(
  definitions: readonly AppFeatureDefinition[],
  parentRoute: AnyRoute,
  context: {
    queryClient: QueryClient;
    services: ServiceContainer;
    permissionCatalog: ReturnType<FeatureRegistry["getPermissionCatalog"]>;
  },
): AnyRoute[] {
  return definitions.map((module) => {
    const FallbackComponent = module.fallback ?? DefaultModuleFallback;
    const ErrorFallbackComponent = module.errorFallback;

    const featureWrapperRoute = createRoute({
      getParentRoute: () => parentRoute,
      id: `module-${module.id}`,
      component: () => (
        <ErrorBoundary
          fallback={({ error, reset }) =>
            ErrorFallbackComponent ? (
              <ErrorFallbackComponent
                error={error}
                featureId={module.id}
                reset={reset}
              />
            ) : (
              <DefaultModuleErrorFallback
                error={error}
                featureId={module.id}
                reset={reset}
              />
            )
          }
        >
          <Suspense fallback={<FallbackComponent />}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      ),
    });

    const childRoutes = module.routes({
      getParentRoute: () => featureWrapperRoute,
      // A remote owns all routing below its base path. The host contributes a
      // single splat route so a remote's internal route tree stays independent
      // from the host's typed route tree.
      basePath:
        module.source?.kind === "remote"
          ? `${module.basePath}/$`
          : module.basePath,
      queryClient: context.queryClient,
      services: context.services,
      permissionCatalog: context.permissionCatalog,
    });

    if (childRoutes.length > 0) {
      return featureWrapperRoute.addChildren(childRoutes);
    }
    return featureWrapperRoute;
  });
}

export interface AppRouterOptions {
  readonly authStore?: StoreApi<AuthState>;
  readonly authController?: AuthController;
  readonly authRepository?: AuthRepository;
  readonly queryClient?: QueryClient;
  readonly registry?: FeatureRegistry;
  readonly services?: MutableServiceContainer;
  readonly history?: RouterHistory;
  readonly remoteModuleLoader?: RemoteFeatureLoader;
  readonly onRemoteModuleFailure?: (failure: RemoteFeatureFailure) => void;
  readonly onModuleManifestFailure?: (error: Error) => void;
  readonly onRuntimeModulesLoaded?: (
    modules: readonly AppFeatureDefinition[],
  ) => void | Promise<void>;
}

export function createAppRouter(options: AppRouterOptions = {}) {
  const authStore = options.authStore ?? useAuthStore;
  const authController = options.authController ?? defaultAuthController;
  const authRepository = options.authRepository ?? defaultAuthRepository;
  const registry = options.registry ?? defaultModuleRegistry;
  const queryClient = options.queryClient ?? createAppQueryClient();
  const services = options.services ?? createServiceContainer();
  const boundModuleVersions = new Map<string, string>();

  function bindModuleServices(): void {
    for (const module of registry.list()) {
      if (
        module.provideServices === undefined ||
        boundModuleVersions.get(module.id) === module.version
      ) {
        continue;
      }

      const bindings = module.provideServices({
        httpClient: authHttpClient,
        queryClient,
        services,
      });
      services.bindAll(bindings);
      boundModuleVersions.set(module.id, module.version);
    }
  }

  bindModuleServices();

  function createRouteTree() {
    const rootRoute = createRootRoute({
      component: () => <Outlet />,
    });

    const authLoadingRoute = createRoute({
      getParentRoute: () => rootRoute,
      path: "/auth/loading",
      validateSearch: (search: Record<string, unknown>) => {
        const pending = safeInternalPath(search["redirect"]);
        return pending === null ? {} : { redirect: pending };
      },
      beforeLoad: ({ search }) => {
        const state = authStore.getState();
        if (state.initialized) {
          if (state.accessToken !== null && state.user !== null) {
            throwRedirect({ to: "/" });
          }
          throwRedirect({ to: search.redirect ?? "/auth/login" });
        }
      },
      component: function AuthLoadingRouteScreen() {
        // The dynamic route tree leaves useSearch() untyped, so the pending
        // path is read off router state and re-validated here.
        const pending = useRouterState({
          select: (state) =>
            safeInternalPath(
              (state.location.search as Record<string, unknown>)["redirect"],
            ) ?? undefined,
        });
        return (
          <AuthLoadingScreen authStore={authStore} pendingPath={pending} />
        );
      },
    });

    const authLoginRoute = createRoute({
      getParentRoute: () => rootRoute,
      path: "/auth/login",
      beforeLoad: ({ location }) => {
        gateBeforeLoad(resolveGuestGuard(authStore.getState()), location);
      },
      component: () => (
        <LoginScreen
          authController={authController}
          authStore={authStore}
          registry={registry}
        />
      ),
    });

    const otpVerifyRoute = createRoute({
      getParentRoute: () => rootRoute,
      path: "/auth/verify-otp",
      beforeLoad: () => {
        const guard = resolveGuestGuard(authStore.getState());
        if (!guard.allowed) {
          throwRedirect({ to: guard.redirectTo });
        }
        if (!authStore.getState().pendingOtpUsername) {
          throwRedirect({ to: "/auth/login" });
        }
      },
      component: () => (
        <OtpChallengeScreen
          authController={authController}
          authStore={authStore}
          registry={registry}
        />
      ),
    });

    const otpAliasRoute = createRoute({
      getParentRoute: () => rootRoute,
      path: "/auth/otp",
      beforeLoad: () => {
        throwRedirect({ to: "/auth/verify-otp" });
      },
      component: () => null,
    });

    const otpChallengeAliasRoute = createRoute({
      getParentRoute: () => rootRoute,
      path: "/auth/otp-challenge",
      beforeLoad: () => {
        throwRedirect({ to: "/auth/verify-otp" });
      },
      component: () => null,
    });

    const forgotPasswordRoute = createRoute({
      getParentRoute: () => rootRoute,
      path: "/auth/forgot-password",
      beforeLoad: ({ location }) => {
        gateBeforeLoad(resolveGuestGuard(authStore.getState()), location);
      },
      component: () => (
        <ForgotPasswordScreen
          authRepository={authRepository}
          authStore={authStore}
        />
      ),
    });

    const resetOtpVerifyRoute = createRoute({
      getParentRoute: () => rootRoute,
      path: "/auth/verify-reset-otp",
      beforeLoad: () => {
        const guard = resolveGuestGuard(authStore.getState());
        if (!guard.allowed) {
          throwRedirect({ to: guard.redirectTo });
        }
        if (!authStore.getState().pendingPasswordResetEmail) {
          throwRedirect({ to: "/auth/forgot-password" });
        }
      },
      component: () => (
        <ResetOtpChallengeScreen
          authRepository={authRepository}
          authStore={authStore}
        />
      ),
    });

    const resetPasswordRoute = createRoute({
      getParentRoute: () => rootRoute,
      path: "/auth/reset-password",
      beforeLoad: ({ location }) => {
        gateBeforeLoad(resolveGuestGuard(authStore.getState()), location);
        // The resetToken now only ever comes from verify-reset-otp (held in
        // authStore), never a URL -- the old email-link flow is retired.
        if (!authStore.getState().passwordResetToken) {
          throwRedirect({ to: "/auth/forgot-password" });
        }
      },
      component: () => (
        <ResetPasswordScreen
          authRepository={authRepository}
          authStore={authStore}
        />
      ),
    });

    const linkStatusRoute = createRoute({
      getParentRoute: () => rootRoute,
      path: "/auth/link-status",
      beforeLoad: ({ location }) => {
        gateBeforeLoad(resolveGuestGuard(authStore.getState()), location);
      },
      component: () => <LinkStatusScreen />,
    });

    const activateAccountRoute = createRoute({
      getParentRoute: () => rootRoute,
      path: "/activate-account",
      beforeLoad: ({ location }) => {
        gateBeforeLoad(resolveGuestGuard(authStore.getState()), location);
      },
      component: () => (
        <ActivateAccountScreen authRepository={authRepository} />
      ),
    });

    const noAccessRoute = createRoute({
      getParentRoute: () => rootRoute,
      path: "/no-access",
      beforeLoad: ({ location }) => {
        gateBeforeLoad(resolveAuthGuard(authStore.getState()), location);
      },
      component: () => <NoAccessScreen authController={authController} />,
    });

    const unauthorizedRoute = createRoute({
      getParentRoute: () => rootRoute,
      path: "/unauthorized",
      validateSearch: (search: Record<string, unknown>) => ({
        permission:
          typeof search["permission"] === "string"
            ? search["permission"]
            : undefined,
        from: safeInternalPath(search["from"]) ?? undefined,
      }),
      beforeLoad: ({ location }) => {
        gateBeforeLoad(resolveAuthGuard(authStore.getState()), location);
      },
      component: UnauthorizedRouteScreen,
    });

    const authenticatedRoute = createRoute({
      getParentRoute: () => rootRoute,
      id: "_authenticated",
      beforeLoad: ({ location }) => {
        gateBeforeLoad(resolveAuthGuard(authStore.getState()), location);
      },
      component: () => (
        <AppShell
          registry={registry}
          authStore={authStore}
          authController={authController}
          services={services}
        />
      ),
    });

    const indexRoute = createRoute({
      getParentRoute: () => authenticatedRoute,
      path: "/",
      beforeLoad: () => {
        const state = authStore.getState();
        const guard = resolveAuthGuard(state);
        if (!guard.allowed) {
          throwRedirect({ to: guard.redirectTo });
        }
        if (state.user === null) {
          throwRedirect({ to: "/auth/login" });
        }
        const evaluator = createPermissionEvaluator(state.user.permissions);
        const visibleNavigation = resolveFeatureNavigation(
          registry.list(),
          evaluator,
        );
        const user: AuthUser = {
          ...state.user,
          permissions: [...state.user.permissions],
        };
        const target = resolveLandingRoute(user, visibleNavigation);
        throwRedirect({ to: target });
      },
    });

    const permissionCatalog = registry.getPermissionCatalog();
    const featureRouteTrees = createModuleRoutes(
      registry.list(),
      authenticatedRoute,
      { queryClient, services, permissionCatalog },
    );

    return rootRoute.addChildren([
      authLoadingRoute,
      authLoginRoute,
      otpVerifyRoute,
      otpAliasRoute,
      otpChallengeAliasRoute,
      forgotPasswordRoute,
      resetOtpVerifyRoute,
      resetPasswordRoute,
      linkStatusRoute,
      activateAccountRoute,
      noAccessRoute,
      unauthorizedRoute,
      authenticatedRoute.addChildren([indexRoute, ...featureRouteTrees]),
    ]);
  }

  const router = createRouter({
    routeTree: createRouteTree(),
    ...(options.history !== undefined ? { history: options.history } : {}),
    defaultNotFoundComponent: NotFoundScreen,
  });

  // A 403 from the backend means the frontend's own permission check was
  // stale or wrong (a race, a just-revoked grant) -- leave whatever screen
  // was mid-request for the same unauthorized page a route guard would have
  // shown. See authHttpClient's onForbidden in ./auth.
  setForbiddenHandler(() => {
    void router.navigate({ to: "/unauthorized" });
  });

  authStore.subscribe(() => {
    router.update({ routeTree: createRouteTree() });
    void router.invalidate();
  });

  registry.subscribe(() => {
    bindModuleServices();
    router.update({ routeTree: createRouteTree() });
    void router.invalidate();
  });

  void loadRuntimeFeatures({
    registry,
    ...(options.remoteModuleLoader === undefined
      ? {}
      : { loader: options.remoteModuleLoader }),
    ...(options.onRemoteModuleFailure === undefined
      ? {}
      : { onFeatureFailure: options.onRemoteModuleFailure }),
    ...(options.onModuleManifestFailure === undefined
      ? {}
      : { onManifestFailure: options.onModuleManifestFailure }),
    ...(options.onRuntimeModulesLoaded === undefined
      ? {}
      : { onFeaturesLoaded: options.onRuntimeModulesLoaded }),
  });

  return router;
}

export type AppRouter = ReturnType<typeof createAppRouter>;
